"use client";

import Image from "next/image";
import { type CSSProperties, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export type ImageZoomProps = {
  src: string;
  alt: string;
  width: number;
  height: number;
  style?: CSSProperties;
  className?: string;
  unoptimized?: boolean;
  priority?: boolean;
};

/**
 * Custom ImageZoom component using Dialog instead of react-medium-image-zoom.
 * Renders image normally, shows zoom cursor on hover, opens dialog on click.
 * ~66KB lighter than fumadocs ImageZoom.
 */
export function ImageZoom({
  src,
  alt,
  width,
  height,
  style,
  className,
  unoptimized,
  priority = false,
}: ImageZoomProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        aria-label={`Zoom image: ${alt}`}
        className="mx-auto block cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={() => setOpen(true)}
        type="button"
      >
        <Image
          alt={alt}
          className={className}
          height={height}
          loading={priority ? undefined : "lazy"}
          priority={priority}
          src={src}
          style={style}
          unoptimized={unoptimized}
          width={width}
        />
      </button>

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogContent
          className="flex max-h-[90vh] max-w-[90vw] items-center justify-center border-none bg-transparent p-0 shadow-none sm:max-w-[90vw]"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">{alt}</DialogTitle>
          <button
            aria-label="Close zoomed image"
            className="cursor-zoom-out focus:outline-none"
            onClick={() => setOpen(false)}
            type="button"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt={alt}
              className="max-h-[85vh] w-auto rounded-lg"
              height={height}
              src={src}
              width={width}
            />
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
